import * as React from "react";
import { 
  Database, Server, CheckCircle, XCircle,
  Loader2, Eye, EyeOff, Key, User, Lock, Hash, Power
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export const LakebaseSettings = () => {
  const [settings, setSettings] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [isSaving, setIsSaving] = React.useState(false);
  const [isTesting, setIsTesting] = React.useState(false);
  const [testResult, setTestResult] = React.useState(null);
  const [isEnabled, setIsEnabled] = React.useState(true);
  
  // Form state
  const [host, setHost] = React.useState("");
  const [port, setPort] = React.useState(5432);
  const [database, setDatabase] = React.useState("");
  const [username, setUsername] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [showPassword, setShowPassword] = React.useState(false);
  const [hasChanges, setHasChanges] = React.useState(false);

  React.useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/lakebase/settings`, { withCredentials: true });
      setSettings(response.data);
      setHost(response.data.host || "");
      setPort(response.data.port || 5432);
      setDatabase(response.data.database || "");
      setUsername(response.data.username || "");
      setIsEnabled(response.data.is_enabled !== false);
    } catch (error) {
      console.error("Error loading settings:", error);
      toast.error("Error al cargar configuración de Lakebase");
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggle = async (enabled) => {
    try {
      await axios.put(`${API}/admin/lakebase/toggle`, { enabled }, { withCredentials: true });
      setIsEnabled(enabled);
      toast.success(enabled ? "Lakebase habilitado" : "Lakebase deshabilitado");
    } catch (error) {
      console.error("Error toggling Lakebase:", error);
      toast.error("Error al cambiar estado de Lakebase");
    }
  };

  const handleSave = async () => {
    if (!host || !database || !username || !password) {
      toast.error("Todos los campos son requeridos");
      return;
    }

    setIsSaving(true);
    try {
      await axios.put(`${API}/admin/lakebase/settings`, {
        host,
        port: parseInt(port),
        database,
        username,
        password
      }, { withCredentials: true });
      
      toast.success("Credenciales de Lakebase guardadas");
      setHasChanges(false);
      setPassword("");
      loadSettings();
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error(error.response?.data?.detail || "Error al guardar credenciales");
    } finally {
      setIsSaving(false);
    }
  };

  const handleTest = async () => {
    setIsTesting(true);
    setTestResult(null);
    
    try {
      const testWithNew = hasChanges && host && database && username && password;
      
      let response;
      if (testWithNew) {
        response = await axios.post(`${API}/admin/lakebase/test`, {
          host,
          port: parseInt(port),
          database,
          username,
          password
        }, { withCredentials: true });
      } else {
        response = await axios.post(`${API}/admin/lakebase/test`, null, { withCredentials: true });
      }
      
      setTestResult(response.data);
      
      if (response.data.success) {
        toast.success("Conexión exitosa");
      } else {
        toast.error("Error de conexión");
      }
    } catch (error) {
      console.error("Error testing connection:", error);
      setTestResult({
        success: false,
        message: error.response?.data?.detail || "Error al probar la conexión"
      });
      toast.error("Error al probar la conexión");
    } finally {
      setIsTesting(false);
    }
  };

  const handleFieldChange = (setter) => (e) => {
    setter(e.target.value);
    setHasChanges(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Enable/Disable Switch */}
      <div className={`flex items-center justify-between p-4 rounded-lg border ${
        isEnabled ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200'
      }`}>
        <div className="flex items-center gap-3">
          <Power className={`h-5 w-5 ${isEnabled ? 'text-emerald-600' : 'text-slate-400'}`} />
          <div>
            <p className="font-medium text-slate-800">
              {isEnabled ? 'Lakebase Habilitado' : 'Lakebase Deshabilitado'}
            </p>
            <p className="text-sm text-slate-500">
              {isEnabled 
                ? 'Los módulos pueden usar esta conexión' 
                : 'Ningún módulo puede usar esta conexión'}
            </p>
          </div>
        </div>
        <Switch
          checked={isEnabled}
          onCheckedChange={handleToggle}
        />
      </div>

      {/* Connection Form */}
      <div className={`space-y-4 ${!isEnabled ? 'opacity-50 pointer-events-none' : ''}`}>
        <div className="flex items-center gap-2 mb-4">
          {settings?.is_configured ? (
            <Badge className="bg-emerald-100 text-emerald-700">Configurado</Badge>
          ) : (
            <Badge variant="outline" className="text-slate-500">No configurado</Badge>
          )}
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          <div className="md:col-span-2 space-y-2">
            <Label htmlFor="host" className="flex items-center gap-2">
              <Server className="h-4 w-4" />
              Host
            </Label>
            <Input
              id="host"
              placeholder="your-lakebase-host.databricks.com"
              value={host}
              onChange={handleFieldChange(setHost)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="port" className="flex items-center gap-2">
              <Hash className="h-4 w-4" />
              Puerto
            </Label>
            <Input
              id="port"
              type="number"
              placeholder="5432"
              value={port}
              onChange={handleFieldChange(setPort)}
            />
          </div>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="database" className="flex items-center gap-2">
            <Database className="h-4 w-4" />
            Base de Datos
          </Label>
          <Input
            id="database"
            placeholder="nombre_base_datos"
            value={database}
            onChange={handleFieldChange(setDatabase)}
          />
        </div>
        
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="username" className="flex items-center gap-2">
              <User className="h-4 w-4" />
              Usuario
            </Label>
            <Input
              id="username"
              placeholder="usuario"
              value={username}
              onChange={handleFieldChange(setUsername)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password" className="flex items-center gap-2">
              <Key className="h-4 w-4" />
              Contraseña
            </Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                placeholder={settings?.is_configured ? "••••••••" : "contraseña"}
                value={password}
                onChange={handleFieldChange(setPassword)}
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {settings?.is_configured && (
              <p className="text-xs text-slate-500">
                Actual: {settings.password_masked}
              </p>
            )}
          </div>
        </div>

        {/* Test Result */}
        {testResult && (
          <Alert className={testResult.success ? "border-emerald-200 bg-emerald-50" : "border-red-200 bg-red-50"}>
            {testResult.success ? (
              <CheckCircle className="h-4 w-4 text-emerald-600" />
            ) : (
              <XCircle className="h-4 w-4 text-red-600" />
            )}
            <AlertTitle className={testResult.success ? "text-emerald-800" : "text-red-800"}>
              {testResult.success ? "Conexión Exitosa" : "Error de Conexión"}
            </AlertTitle>
            <AlertDescription className={testResult.success ? "text-emerald-700" : "text-red-700"}>
              {testResult.message}
              {testResult.tables_found && testResult.tables_found.length > 0 && (
                <div className="mt-2">
                  <span className="font-medium">Tablas encontradas:</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {testResult.tables_found.slice(0, 10).map((table, idx) => (
                      <Badge key={idx} variant="outline" className="text-xs">
                        {table}
                      </Badge>
                    ))}
                    {testResult.tables_found.length > 10 && (
                      <Badge variant="outline" className="text-xs">
                        +{testResult.tables_found.length - 10} más
                      </Badge>
                    )}
                  </div>
                </div>
              )}
            </AlertDescription>
          </Alert>
        )}

        {/* Actions */}
        <div className="flex gap-3">
          <Button
            onClick={handleTest}
            variant="outline"
            disabled={isTesting || (!settings?.is_configured && !hasChanges)}
          >
            {isTesting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Probando...
              </>
            ) : (
              <>
                <CheckCircle className="h-4 w-4 mr-2" />
                Probar Conexión
              </>
            )}
          </Button>
          
          <Button
            onClick={handleSave}
            disabled={isSaving || !hasChanges}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Guardando...
              </>
            ) : (
              <>
                <Lock className="h-4 w-4 mr-2" />
                Guardar Credenciales
              </>
            )}
          </Button>
        </div>

        {/* Info */}
        {settings?.last_updated && (
          <p className="text-xs text-slate-500">
            Última actualización: {new Date(settings.last_updated).toLocaleString('es-ES')} 
            {settings.last_updated_by && ` por ${settings.last_updated_by}`}
          </p>
        )}
      </div>
    </div>
  );
};
